import React, { useEffect, useRef, useState } from "react";
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography } from "@mui/material";
import { AutoAwesome, FormatListNumbered, PeopleOutline } from "@mui/icons-material";
import axios from "axios";
import useApiEp from "../../hooks/useApiEp";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";
import AIPromptTextField from "../campaign/contacts/AIPromptTextField";

export default function RcsDatabaseAudience({ campaignData, setCampaignData, tabState, setTabState, contactGroupName }) {
  const { data: lists, isLoading, isError, refetch } = useApiEp("/user/get-my-assigned-dbs");
  const draft = tabState.databaseAudience || { listId: "", mode: "ai", start: "1", end: "", prompt: "", result: null };
  const selectedList = (Array.isArray(lists) ? lists : []).find((list) => list._id === draft.listId) || null;
  const { data: countData, isLoading: counting, isError: countError, refetch: recount } = useApiEp(
    `/database/get-list-count?listId=${encodeURIComponent(draft.listId)}`, !!draft.listId
  );
  const total = countData?.count;
  const [loadingAI, setLoadingAI] = useState(false);
  const [error, setError] = useState("");
  const requestVersion = useRef(0);
  useEffect(() => () => { requestVersion.current += 1; }, []);

  useEffect(() => {
    if (!tabState.databaseAudience && contactGroupName && Array.isArray(lists)) {
      const list = lists.find((item) => item.name === contactGroupName || item._id === contactGroupName);
      if (list) setTabState((previous) => ({ ...previous, databaseAudience: {
        listId: list._id, mode: "ai", start: "1", end: "", prompt: "", result: null,
      } }));
    }
  }, [contactGroupName, lists, setTabState, tabState.databaseAudience]);

  const clearConfirmed = () => setCampaignData((previous) => ({
    ...previous, selectedGroup: "", selectedGroupLabel: "", audienceCount: undefined,
    contactSelectionMode: undefined, dbquery: undefined, startIndex: undefined, endIndex: undefined,
    contacts: [],
  }));
  const updateDraft = (patch) => {
    requestVersion.current += 1;
    setLoadingAI(false);
    setError("");
    setTabState((previous) => ({ ...previous, databaseAudience: { ...draft, ...patch, result: null } }));
    clearConfirmed();
  };
  const start = Number(draft.start);
  const end = Number(draft.end);
  const validRange = Number.isSafeInteger(start) && Number.isSafeInteger(end) && start >= 1 && end >= start && Number.isFinite(total) && end <= total;
  const count = draft.mode === "ai" ? draft.result?.count : validRange ? end - start + 1 : null;
  const ready = !!selectedList && !loadingAI && (draft.mode === "ai" ? !!draft.result?.fullFilter && count > 0 : validRange);
  const confirmed = campaignData.dbGroup === "database" && campaignData.selectedGroup === draft.listId && !!campaignData.contactSelectionMode;

  const askAI = async (prompt) => {
    if (!draft.listId || !prompt.trim()) return;
    const version = ++requestVersion.current;
    clearConfirmed();
    setLoadingAI(true);
    setError("");
    setTabState((previous) => ({ ...previous, databaseAudience: { ...draft, result: null } }));
    try {
      const response = await axios.post(`${API_ENDPOINT}/database/database-ai-assistant`, { id: draft.listId, prompt }, getHeader());
      if (version !== requestVersion.current) return;
      if (!response.data.fullFilter?.filter || !Number.isSafeInteger(response.data.count) || response.data.count < 0) {
        throw new Error("Unable to create a valid audience. Please try again.");
      }
      setTabState((previous) => ({ ...previous, databaseAudience: { ...draft, result: response.data } }));
    } catch (failure) {
      if (version === requestVersion.current) setError(failure.response?.data?.message || failure.response?.data?.error || failure.message || "AI selection failed. Please try again.");
    } finally {
      if (version === requestVersion.current) setLoadingAI(false);
    }
  };
  const confirm = () => {
    if (!ready) return;
    setCampaignData((previous) => ({
      ...previous, contacts: [], selectedGroup: draft.listId, selectedGroupLabel: selectedList.name,
      dbGroup: "database", contactSelectionMode: draft.mode, audienceCount: count,
      dbquery: draft.mode === "ai" ? draft.result.fullFilter : undefined,
      startIndex: draft.mode === "range" ? start : undefined,
      endIndex: draft.mode === "range" ? end : undefined,
    }));
  };

  return (
    <Stack spacing={2.5} sx={{ py: 3, px: { xs: 0, sm: 1 } }}>
      <Box>
        <Typography variant="h6" fontWeight={700}>Build your database audience</Typography>
        <Typography variant="body2" color="text.secondary">Choose a database, then select the contacts for this campaign.</Typography>
      </Box>
      {isError && <Alert severity="error" action={<Button onClick={refetch}>Retry</Button>}>Could not load your databases.</Alert>}
      {!isLoading && !isError && Array.isArray(lists) && !lists.length && <Alert severity="info">No databases are assigned to your account yet.</Alert>}
      <Autocomplete
        options={Array.isArray(lists) ? lists : []} loading={isLoading} value={selectedList}
        getOptionLabel={(option) => option.name || "Untitled database"}
        isOptionEqualToValue={(option, value) => option._id === value._id}
        onChange={(_, list) => updateDraft({ listId: list?._id || "", start: "1", end: "", prompt: "" })}
        renderInput={(params) => <TextField {...params} label="Database" placeholder="Search your assigned databases" />}
      />
      {selectedList && <>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip icon={<PeopleOutline />} label={counting ? "Counting contacts…" : Number.isFinite(total) ? `${total.toLocaleString()} contacts available` : "Contact count unavailable"} variant="outlined" />
          <Typography variant="caption" color="text.secondary">Phone validation and duplicate removal apply when launching.</Typography>
        </Stack>
        {countError && <Alert severity="error" action={<Button onClick={recount}>Retry</Button>}>Could not load the database size.</Alert>}
        <Box role="group" aria-label="Database selection method" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
          {[
            { value: "ai", title: "AI assistance", description: "Describe the contacts you want to reach.", Icon: AutoAwesome },
            { value: "range", title: "Simple range", description: "Choose contacts by their position in the list.", Icon: FormatListNumbered },
          ].map(({ value, title, description, Icon }) => <Button
            key={value} aria-pressed={draft.mode === value} onClick={() => { if (draft.mode !== value) updateDraft({ mode: value }); }}
            variant="outlined" sx={{ p: 2, textAlign: "left", justifyContent: "flex-start", textTransform: "none", borderRadius: 2,
              borderColor: draft.mode === value ? "primary.main" : "divider", bgcolor: draft.mode === value ? "action.selected" : "background.paper" }}
            startIcon={<Icon />}>
            <Box><Typography component="span" display="block" fontWeight={700}>{title}</Typography><Typography component="span" display="block" variant="caption" color="text.secondary">{description}</Typography></Box>
          </Button>)}
        </Box>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          {draft.mode === "ai" ? <Stack spacing={2}>
            <Typography fontWeight={600}>Who would you like to reach?</Typography>
            {selectedList.columns?.length > 0 && <Box>
              <Typography variant="caption" color="text.secondary">Available fields</Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75} sx={{ mt: 1 }}>{selectedList.columns.map((column) => <Chip key={column} label={column} size="small" variant="outlined" />)}</Stack>
            </Box>}
            <AIPromptTextField value={draft.prompt} onChange={(prompt) => updateDraft({ prompt })} onSend={askAI}
              placeholder="For example: select the latest 100 contacts from Bangalore" loading={loadingAI} variant="outlined" suggestions={[]} showSendButton={false} />
            <Button variant="outlined" startIcon={<AutoAwesome />} disabled={loadingAI || !draft.prompt.trim()} onClick={() => askAI(draft.prompt)} sx={{ alignSelf: "flex-start" }}>Generate audience</Button>
            {loadingAI && <Typography role="status" variant="body2" color="text.secondary"><CircularProgress size={14} sx={{ mr: 1 }} />Finding matching contacts…</Typography>}
            {error && <Alert severity="error">{error}</Alert>}
            {draft.result && <Alert severity={count > 0 ? "success" : "warning"}>{count.toLocaleString()} contacts matched.{count === 0 ? " Try a broader selection." : " Confirm this audience below to continue."}</Alert>}
          </Stack> : <Stack spacing={2}>
            <Typography fontWeight={600}>Select a contact range</Typography>
            <Typography variant="body2" color="text.secondary">Positions start at 1. Both the first and last position are included.</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField fullWidth label="First position" type="number" value={draft.start} onChange={(event) => updateDraft({ start: event.target.value })} inputProps={{ min: 1, max: total, step: 1 }} />
              <TextField fullWidth label="Last position" type="number" value={draft.end} onChange={(event) => updateDraft({ end: event.target.value })} inputProps={{ min: start || 1, max: total, step: 1 }} />
            </Stack>
            <Button sx={{ alignSelf: "flex-start" }} disabled={!total} onClick={() => updateDraft({ start: "1", end: String(total) })}>Use entire database</Button>
            {draft.end !== "" && !validRange && !counting && <Alert severity="warning">Enter whole-number positions from 1 to {total ?? "the database size"}, with the last position at or after the first.</Alert>}
            {validRange && <Alert severity="info">{count.toLocaleString()} contacts selected · positions {start.toLocaleString()}–{end.toLocaleString()}</Alert>}
          </Stack>}
        </Paper>
        <Button variant="contained" size="large" onClick={confirm} disabled={!ready || confirmed} sx={{ alignSelf: { xs: "stretch", sm: "flex-start" }, borderRadius: 2 }}>
          {confirmed ? "Audience confirmed" : `Use this audience${count > 0 ? ` · ${count.toLocaleString()} contacts` : ""}`}
        </Button>
        {confirmed && <Alert severity="success">{selectedList.name} is ready. Continue to review your campaign.</Alert>}
      </>}
    </Stack>
  );
}
